import { useAuthSession } from "~/auth/AuthSessionProvider";
import type {
  Routine,
  RoutineScope,
} from "~/pydantic_models/services/models/dtos/routine";
import { useFeatureFlag } from "~/services/featureFlags";
import { FEATURE_FLAGS } from "~/services/featureFlags/constants";
import { AuthUserGroup } from "~/types/auth";

export const ROUTINE_ROUTE_GROUPS: AuthUserGroup[] = [
  AuthUserGroup.ADMIN,
  AuthUserGroup.MEDICAL_ASSISTANT,
];

export interface RoutineAccess {
  canUseRoutines: boolean;
  loading: boolean;
  /** Cognito sub; routine ownership is recorded by sub, not User.id. */
  cognitoSub: string;
  canCreate: (scope: RoutineScope) => boolean;
  canManage: (routine: Routine) => boolean;
}

/**
 * Mirrors RoutinePolicy (services/routine/policy.py) to decide which controls
 * to show. UX only: every route reauthorizes, and a hidden control is never
 * the reason an operation is refused.
 */
export function useRoutineAccess(): RoutineAccess {
  const { userGroup, authSession } = useAuthSession();
  const chatbot = useFeatureFlag(FEATURE_FLAGS.ENABLED_CHATBOT);
  const routines = useFeatureFlag(FEATURE_FLAGS.ENABLE_INDY_ROUTINES);
  const cognitoSub = authSession?.tokens?.accessToken.payload.sub ?? "";
  const isAdmin = userGroup === AuthUserGroup.ADMIN;
  const staff = userGroup !== null && ROUTINE_ROUTE_GROUPS.includes(userGroup);

  return {
    canUseRoutines: staff && chatbot.isEnabled && routines.isEnabled,
    loading: userGroup === null || chatbot.loading || routines.loading,
    cognitoSub,
    canCreate: (scope) => scope === "PERSONAL" || isAdmin,
    canManage: (routine) =>
      routine.scope === "ORG" ? isAdmin : routine.ownerUserId === cognitoSub,
  };
}
